import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Enrollment, Client } from '../types';
import './Pages.css';

interface ReceiptUploadUrlResponse {
  fileUrl: string;
  uploadUrl: string;
  fileKey: string;
}

interface AddManualTransactionProps {
  onClose: () => void;
  onSuccess: () => void;
}

type TransactionSource = 'LojaFisica' | 'Telefone' | 'Ifood' | 'Delivery' | 'Resgate';

interface CreateManualTransactionDto {
  clientId: string;
  restaurantId: string;
  points?: number; // For debit: direct points value
  amount?: number; // For credit: bill amount (points will be calculated)
  transactionDate: string;
  source?: TransactionSource; // Required only for credit
  receiptPhotoUrl?: string;
  notes?: string;
  isDebit: boolean;
}

const AddManualTransaction: React.FC<AddManualTransactionProps> = ({ onClose, onSuccess }) => {
  const [clients, setClients] = useState<Array<Enrollment & { client?: Client }>>([]);
  const [filteredClients, setFilteredClients] = useState<Array<Enrollment & { client?: Client }>>([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  const [pointsInput, setPointsInput] = useState<string>('');
  const [amountInput, setAmountInput] = useState<string>('');
  
  const [formData, setFormData] = useState<CreateManualTransactionDto>({
    clientId: '',
    restaurantId: '',
    transactionDate: new Date().toISOString().split('T')[0],
    source: 'LojaFisica',
    receiptPhotoUrl: '',
    notes: '',
    isDebit: false
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await api.get<Enrollment[]>('/admin/clients');
      
      // Para cada enrollment, buscar dados do cliente
      const clientsWithDetails = await Promise.all(
        response.data.map(async (enrollment) => {
          try {
            const clientResponse = await api.get<{ client: Client }>(`/admin/clients/${enrollment.clientId}/details`);
            return {
              ...enrollment,
              client: clientResponse.data.client
            };
          } catch (error) {
            console.error(`Error loading client ${enrollment.clientId}:`, error);
            return enrollment;
          }
        })
      );
      
      setClients(clientsWithDetails);
      setFilteredClients(clientsWithDetails);
    } catch (error) {
      console.error('Error loading clients:', error);
      alert('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!clientSearch.trim()) {
      setFilteredClients(clients);
      return;
    }

    const searchLower = clientSearch.toLowerCase().trim();
    const filtered = clients.filter((enrollment) => {
      const client = enrollment.client;
      if (!client) return false;

      const name = (client.name || '').toLowerCase();
      const phone = (client.phone || '').toLowerCase();
      const cpf = (client.cpf || '').toLowerCase();
      
      return name.includes(searchLower) || 
             phone.includes(searchLower) || 
             cpf.includes(searchLower);
    });

    setFilteredClients(filtered);
    setShowClientDropdown(true);
  }, [clientSearch, clients]);

  const handleClientSelect = (enrollment: Enrollment & { client?: Client }) => {
    setFormData({...formData, clientId: enrollment.clientId});
    setClientSearch(enrollment.client?.name || enrollment.clientId);
    setShowClientDropdown(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const uploadReceipt = async (): Promise<string | null> => {
    if (!selectedFile) return null;

    try {
      setUploading(true);
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);

      // Use the same endpoint as client (or create a shared one)
      const response = await api.post<ReceiptUploadUrlResponse>('/client/receipts/upload', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      return response.data.fileUrl;
    } catch (error) {
      console.error('Error uploading receipt:', error);
      alert('Erro ao fazer upload da foto');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.clientId) {
      alert('Por favor, selecione um cliente');
      return;
    }

    try {
      setLoading(true);

      // Validação baseada no tipo (crédito ou débito)
      if (formData.isDebit) {
        // Débito: validar pontos
        const pointsValue = parseInt(pointsInput) || 0;
        if (pointsValue <= 0) {
          alert('Por favor, informe a quantidade de pontos a debitar (deve ser maior que zero)');
          setLoading(false);
          return;
        }
      } else {
        // Crédito: validar valor
        const amountValue = parseFloat(amountInput) || 0;
        if (amountValue <= 0) {
          alert('Por favor, informe o valor da conta (deve ser maior que zero)');
          setLoading(false);
          return;
        }
        if (!formData.source) {
          alert('Por favor, selecione a origem da transação');
          setLoading(false);
          return;
        }
      }

      // Upload photo if selected
      let photoUrl: string | undefined;
      if (selectedFile) {
        photoUrl = await uploadReceipt() || undefined;
      }

      const payload: CreateManualTransactionDto = {
        clientId: formData.clientId,
        restaurantId: '', // Será preenchido pelo backend
        points: formData.isDebit ? parseInt(pointsInput) || 0 : undefined,
        amount: !formData.isDebit ? parseFloat(amountInput) || 0 : undefined,
        transactionDate: formData.transactionDate,
        source: formData.isDebit ? undefined : formData.source,
        receiptPhotoUrl: photoUrl,
        notes: formData.notes,
        isDebit: formData.isDebit
      };

      await api.post('/admin/transactions/manual', payload);
      
      alert('Transação cadastrada com sucesso!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error creating transaction:', error);
      const message = error.response?.data?.message || 'Erro ao cadastrar transação';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedClient = clients.find(c => c.clientId === formData.clientId);

  useEffect(() => {
    if (selectedClient) {
      setClientSearch(selectedClient.client?.name || selectedClient.clientId);
    }
  }, [selectedClient]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.form-group')) {
        setShowClientDropdown(false);
      }
    };

    if (showClientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showClientDropdown]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' }}>
          <h2>Adicionar Transação Manual</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group" style={{ position: 'relative' }}>
            <label>Cliente *</label>
            <input
              type="text"
              required
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value);
                if (!e.target.value) {
                  setFormData({...formData, clientId: ''});
                }
              }}
              onFocus={() => setShowClientDropdown(true)}
              placeholder="Buscar por nome, telefone ou CPF..."
              style={{ width: '100%', padding: '14px 16px', border: '2px solid var(--border)', borderRadius: '12px', fontSize: '15px' }}
            />
            {showClientDropdown && filteredClients.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '2px solid var(--border)',
                borderTop: 'none',
                borderRadius: '0 0 12px 12px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}>
                {filteredClients.map((enrollment) => (
                  <div
                    key={enrollment.enrollmentId}
                    onClick={() => handleClientSelect(enrollment)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--bg-gray)',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-gray)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    <div style={{ fontWeight: 600, color: 'var(--text-dark)' }}>
                      {enrollment.client?.name || enrollment.clientId}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-gray)', marginTop: '4px' }}>
                      {enrollment.client?.phone && `📱 ${enrollment.client.phone}`}
                      {enrollment.client?.cpf && ` • CPF: ${enrollment.client.cpf}`}
                      {` • ${enrollment.currentPoints} pts`}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {selectedClient && (
              <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '4px', fontSize: '14px' }}>
                <strong>Cliente selecionado:</strong> {selectedClient.client?.name || selectedClient.clientId}
                <br />
                <strong>Pontos atuais:</strong> {selectedClient.currentPoints}
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Data da Transação *</label>
              <input
                type="date"
                required
                value={formData.transactionDate}
                onChange={(e) => setFormData({...formData, transactionDate: e.target.value})}
              />
            </div>

            {!formData.isDebit && (
              <div className="form-group">
                <label>Origem *</label>
                <select
                  required
                  value={formData.source || 'LojaFisica'}
                  onChange={(e) => setFormData({...formData, source: e.target.value as TransactionSource})}
                >
                  <option value="LojaFisica">Loja Física</option>
                  <option value="Telefone">Telefone</option>
                  <option value="Ifood">iFood</option>
                  <option value="Delivery">Delivery</option>
                </select>
              </div>
            )}
          </div>

          {formData.isDebit ? (
            // Modo Débito: Pontos diretos
            <div className="form-group">
              <label>
                Pontos a Debitar *
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px', fontWeight: 'normal' }}>
                  ⚠️ Pontos serão DESCONTADOS do cliente (valor negativo)
                </span>
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{
                  position: 'absolute',
                  left: '16px',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#DC2626',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}>
                  -
                </span>
                <input
                  type="number"
                  required
                  min="1"
                  step="1"
                  value={pointsInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPointsInput(value);
                  }}
                  placeholder=""
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              <div className="info-box" style={{ marginTop: '8px' }}>
                💡 Informe a quantidade de pontos que serão descontados do cliente.
              </div>
            </div>
          ) : (
            // Modo Crédito: Valor decimal
            <div className="form-group">
              <label>
                Valor da Conta (R$) *
                <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px', fontWeight: 'normal' }}>
                  ✓ Pontos serão calculados automaticamente baseado no valor
                </span>
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <span style={{
                  position: 'absolute',
                  left: '16px',
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#2563EB',
                  zIndex: 1,
                  pointerEvents: 'none'
                }}>
                  +
                </span>
                <input
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  value={amountInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAmountInput(value);
                  }}
                  placeholder="0.00"
                  style={{ paddingLeft: '40px' }}
                />
              </div>
              <div className="info-box" style={{ marginTop: '8px' }}>
                💡 Informe o valor da conta. Os pontos serão calculados automaticamente baseado no plano de fidelidade do cliente.
              </div>
            </div>
          )}

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isDebit}
                onChange={(e) => {
                  const isDebit = e.target.checked;
                  setFormData({
                    ...formData, 
                    isDebit,
                    source: isDebit ? undefined : (formData.source || 'LojaFisica')
                  });
                  // Reset inputs when switching between credit/debit
                  setPointsInput('');
                  setAmountInput('');
                }}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <span>Debitar pontos do cliente (valor negativo)</span>
            </label>
          </div>

          <div className="form-group">
            <label>Foto da Nota Fiscal (Opcional)</label>
            <div className="file-input">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
              />
              {selectedFile && <span className="file-name">📷 {selectedFile.name}</span>}
            </div>
          </div>

          <div className="form-group">
            <label>Observações (Opcional)</label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Observações sobre a transação..."
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading || uploading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || uploading}
            >
              {loading || uploading ? 'Processando...' : '✓ Cadastrar Transação'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddManualTransaction;

